
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { cn } from "./utils";
import PropTypes from "prop-types";
import { Spinner } from "./loading/Spinner";

const buttonVariants = cva(
"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
 {
 variants: {
 variant: {
 default:"bg-primary text-primary-foreground hover:bg-primary/90",
 destructive:
"bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20",
 outline:
"border bg-background text-foreground hover:bg-accent hover:text-accent-foreground",
 secondary:
"bg-secondary text-secondary-foreground hover:bg-secondary/80",
 ghost:
"hover:bg-accent hover:text-accent-foreground",
 link:"text-primary underline-offset-4 hover:underline",
 },
 size: {
 default:"h-9 px-4 py-2 has-[>svg]:px-3",
 sm:"h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
 lg:"h-10 rounded-md px-6 has-[>svg]:px-4",
 icon:"size-9 rounded-md",
 },
 },
 defaultVariants: {
 variant:"default",
 size:"default",
 },
 },
);


// Filled variants get a white spinner, the rest a gray one.
const WHITE_SPINNER_VARIANTS = new Set([undefined, null, "default", "destructive"]);

// While loading, the leading icon (first child, if it is an element) is
// swapped for the spinner.
function withoutLeadingIcon(children) {
  const items = React.Children.toArray(children);
  return React.isValidElement(items[0]) && typeof items[0].type !== "string"
    ? items.slice(1)
    : items;
}

// forwardRef so Radix `asChild` triggers (DropdownMenu, Popover, Tooltip) can anchor to the DOM node.
const Button = React.forwardRef(function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  loadingText,
  disabled,
  children,
  ...props
}, ref) {
  const Comp = asChild ? Slot : "button";
  const content =
    loading && !asChild ? (
      <>
        <Spinner tone={WHITE_SPINNER_VARIANTS.has(variant) ? "white" : "gray"} />
        {loadingText ?? withoutLeadingIcon(children)}
      </>
    ) : (
      children
    );
  return (
    <Comp
      ref={ref}
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {content}
    </Comp>
  );
});

Button.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.string,
  size: PropTypes.string,
  asChild: PropTypes.bool,
  loading: PropTypes.bool,
  loadingText: PropTypes.node,
};

export { Button, buttonVariants };
